// Creation / rename dialogs for the hierarchy, driven by a tiny global store so any
// menu can open them.
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { useNavigate } from 'react-router-dom';
import { Check, Lock } from '@/components/ui/icons';
import { PALETTE } from '@shared/constants';
import type { Location } from '@shared/types';
import { createChatSection, createFolder, createList, createSpace, renameEntity } from '@/lib/actions';
import { locationPath, useWorkspaceId } from '@/lib/nav';
import { readableOn } from '@/lib/color';
import { Modal } from '@/components/ui/Modal';
import { toggleExpanded } from '@/lib/ui';
import { StatusEditorModal } from './StatusEditor';

type Dialog =
  | { kind: 'space' }
  | { kind: 'folder'; spaceId: string }
  | { kind: 'list'; spaceId: string; folderId: string | null }
  | { kind: 'rename'; c: 'spaces' | 'folders' | 'lists' | 'views' | 'docs' | 'chatSections'; id: string; name: string }
  /** a Chat sidebar section, optionally moving a conversation into it */
  | { kind: 'chat-section'; workspaceId: string; channelId?: string }
  | { kind: 'statuses'; target: { type: 'space' | 'folder' | 'list'; id: string } }
  | null;

export const useDialog = create<{ dialog: Dialog }>(() => ({ dialog: null }));
export const openDialog = (d: Dialog) => useDialog.setState({ dialog: d });
const close = () => useDialog.setState({ dialog: null });

function CreateSpace() {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[3]);
  const [priv, setPriv] = useState(false);
  const submit = () => {
    if (!name.trim()) return;
    const { space, list } = createSpace(ws, { name, color, private: priv });
    toggleExpanded(`space:${space.id}`, true);
    close();
    navigate(locationPath(ws, { type: 'list', id: list.id }));
  };
  return (
    <Modal
      open
      onClose={close}
      title="Create a Space"
      width={520}
      footer={
        <button className="btn primary" disabled={!name.trim()} onClick={submit}>
          Continue
        </button>
      }
    >
      <p className="muted" style={{ marginBottom: 16, fontSize: 'var(--fs-md)' }}>
        A Space represents teams, departments, or groups, each with its own Lists, workflows, and settings.
      </p>
      <div className="form-field">
        <label>Icon & name</label>
        <div className="row gap-8">
          <span className="space-avatar" style={{ width: 32, height: 32, background: color, color: readableOn(color), fontSize: 16, borderRadius: 8 }}>
            {(name.trim()[0] ?? 'S').toUpperCase()}
          </span>
          <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Marketing, Engineering, HR" onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </div>
      </div>
      <div className="form-field">
        <label>Color</label>
        <div className="swatches">
          {PALETTE.map((c) => (
            <button key={c} className="swatch" style={{ background: c }} onClick={() => setColor(c)}>
              {c === color && <Check size={12} />}
            </button>
          ))}
        </div>
      </div>
      <div className="row gap-8" style={{ marginTop: 8 }}>
        <Lock size={15} className="faint" />
        <div className="grow">
          <div style={{ fontSize: 'var(--fs-base)', fontWeight: 500 }}>Make Private</div>
          <div className="faint" style={{ fontSize: 'var(--fs-sm)' }}>
            Only you and invited members have access
          </div>
        </div>
        <button className="toggle" data-on={priv} onClick={() => setPriv(!priv)} aria-label="Private" />
      </div>
    </Modal>
  );
}

function NameDialog({ title, placeholder, initial = '', cta, onSubmit }: { title: string; placeholder: string; initial?: string; cta: string; onSubmit: (name: string) => void }) {
  const [name, setName] = useState(initial);
  useEffect(() => setName(initial), [initial]);
  const submit = () => {
    if (!name.trim()) return;
    onSubmit(name.trim());
    close();
  };
  return (
    <Modal
      open
      onClose={close}
      title={title}
      width={460}
      footer={
        <>
          <button className="btn outline" onClick={close}>
            Cancel
          </button>
          <button className="btn primary" disabled={!name.trim()} onClick={submit}>
            {cta}
          </button>
        </>
      }
    >
      <div className="form-field">
        <label>Name</label>
        <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === 'Enter' && submit()} onFocus={(e) => e.target.select()} />
      </div>
    </Modal>
  );
}

export function DialogHost() {
  const dialog = useDialog((s) => s.dialog);
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  if (!dialog) return null;
  const goto = (loc: Location) => navigate(locationPath(ws, loc));
  switch (dialog.kind) {
    case 'space':
      return <CreateSpace />;
    case 'folder':
      return (
        <NameDialog
          title="Create Folder"
          placeholder="Enter Folder name"
          cta="Create"
          onSubmit={(name) => {
            const f = createFolder(dialog.spaceId, name);
            toggleExpanded(`space:${dialog.spaceId}`, true);
            toggleExpanded(`folder:${f.id}`, true);
            goto({ type: 'folder', id: f.id });
          }}
        />
      );
    case 'list':
      return (
        <NameDialog
          title="Create List"
          placeholder="Enter List name"
          cta="Create"
          onSubmit={(name) => {
            const l = createList(dialog.spaceId, dialog.folderId, name);
            toggleExpanded(`space:${dialog.spaceId}`, true);
            if (dialog.folderId) toggleExpanded(`folder:${dialog.folderId}`, true);
            goto({ type: 'list', id: l.id });
          }}
        />
      );
    case 'rename':
      return <NameDialog title="Rename" placeholder="Name" initial={dialog.name} cta="Save" onSubmit={(name) => renameEntity(dialog.c, dialog.id, name)} />;
    case 'statuses':
      return <StatusEditorModal target={dialog.target} onClose={close} />;
    case 'chat-section':
      return (
        <NameDialog
          title="Create a section"
          placeholder="e.g. Clients, Projects, Internal"
          cta="Create"
          onSubmit={(name) => {
            createChatSection(dialog.workspaceId, name, dialog.channelId);
          }}
        />
      );
  }
}
